import { backend } from "@/integrations/backend/client";
import { loadDailyStreakBonusTotals } from "../core/daily-streak-bonus";
import {
  getQuestionBankStationIdsForIsland,
  isQuestionBankStationForIsland,
} from "./question-bank-route";
import {
  getPlaytestProgressKey,
  loadStoredPlaytestProgress,
} from "./playtest-progress";

export const FINAL_ASSESSMENT_PASS_RATE = 90;
export const VICTORY_DISSOLUTION_DAYS = 10;
const VICTORY_DECLARATIONS_TABLE = "crew_victory_declarations";
const LOCAL_DECLARATIONS_KEY = "lina:crew-victory-declarations";

interface CrewRecord {
  id: string;
  name: string;
  owner_id: string;
  created_at?: string;
}

interface CrewMemberRecord {
  crew_id: string;
  user_id: string;
}

interface ProfileRecord {
  id: string;
  display_name: string | null;
  avatar_url: string | null;
  hub: string | null;
}

interface IslandRecord {
  id: string;
  slug: string;
  crew_id: string | null;
  is_active?: boolean;
  order_index?: number;
}

interface StationRecord {
  id: string;
  island_id: string;
  slug?: string | null;
  source_station_id?: string | null;
}

interface IslandProgressRecord {
  user_id: string;
  island_id: string;
  score: number;
  completed_at: string;
}

interface StationProgressRecord {
  user_id: string;
  station_id: string;
  station_points: number;
  completed_at?: string | null;
}

interface SelfAssessmentAttemptRecord {
  user_id: string;
  score: number;
  total_questions: number;
  completed_at: string;
  assessment_type?: "initial" | "final" | string | null;
}

interface CrewVictoryDeclarationRecord {
  crew_id: string;
  user_id: string;
  declared_at: string;
  dissolves_at?: string | null;
}

export interface CrewVictoryMember {
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
  hub: string | null;
  total_score: number;
  daily_streak_bonus_score: number;
  islands_completed: number;
  total_islands: number;
  completed_all_islands: boolean;
  island_completion_at: string | null;
  final_assessment_score: number | null;
  final_assessment_total: number | null;
  final_assessment_rate: number | null;
  final_assessment_completed_at: string | null;
  final_assessment_passed: boolean;
  victory_declared: boolean;
  victory_declared_at: string | null;
}

export interface CrewVictoryStatus {
  crew_id: string;
  crew_name: string;
  owner_id: string;
  is_solo: boolean;
  member_count: number;
  total_score: number;
  total_islands: number;
  completed_all_islands: boolean;
  crew_completion_at: string | null;
  final_assessment_complete: boolean;
  victory_declaration_open: boolean;
  winner_declared: boolean;
  declared_at: string | null;
  victory_dissolves_at: string | null;
  victory_dissolved: boolean;
  members: CrewVictoryMember[];
}

export interface CrewVictoryBoard {
  crews: CrewVictoryStatus[];
  winner: CrewVictoryStatus | null;
  currentUserCrew: CrewVictoryStatus | null;
  declarationStorageAvailable: boolean;
}

function assertNoError(response: { error?: unknown }, label: string) {
  if (response.error) {
    throw new Error(label);
  }
}

function toTime(value: string | null | undefined) {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : 0;
}

function latestDate(values: (string | null)[]) {
  const latest = values.reduce((max, value) => Math.max(max, toTime(value)), 0);
  return latest > 0 ? new Date(latest).toISOString() : null;
}

function addDaysIso(value: string, days: number) {
  const base = toTime(value);
  const start = base > 0 ? base : Date.now();
  return new Date(start + days * 24 * 60 * 60 * 1000).toISOString();
}

function declarationKey(crewId: string, userId: string) {
  return `${crewId}|${userId}`;
}

function readLocalDeclarations() {
  if (typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(LOCAL_DECLARATIONS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? (parsed.filter(
          (item) =>
            item &&
            typeof item.crew_id === "string" &&
            typeof item.user_id === "string" &&
            typeof item.declared_at === "string",
        ) as CrewVictoryDeclarationRecord[])
      : [];
  } catch {
    return [];
  }
}

function writeLocalDeclaration(record: CrewVictoryDeclarationRecord) {
  if (typeof window === "undefined") return;

  const records = readLocalDeclarations();
  const nextRecords = [
    ...records.filter(
      (item) =>
        item.crew_id !== record.crew_id || item.user_id !== record.user_id,
    ),
    record,
  ];
  window.localStorage.setItem(
    LOCAL_DECLARATIONS_KEY,
    JSON.stringify(nextRecords),
  );
}

function declarationMap(records: CrewVictoryDeclarationRecord[]) {
  return new Map(
    records.map((record) => [declarationKey(record.crew_id, record.user_id), record]),
  );
}

function validMemberDeclaration(
  declarations: Map<string, CrewVictoryDeclarationRecord>,
  crewId: string,
  member: Pick<
    CrewVictoryMember,
    "user_id" | "final_assessment_completed_at" | "final_assessment_passed"
  >,
) {
  if (!member.final_assessment_passed) return null;

  const declaration = declarations.get(declarationKey(crewId, member.user_id));
  if (!declaration) return null;

  const finalAssessmentAt = toTime(member.final_assessment_completed_at);
  return toTime(declaration.declared_at) >= finalAssessmentAt
    ? declaration
    : null;
}

async function loadVictoryDeclarations() {
  const localDeclarations = readLocalDeclarations();

  try {
    const response = await backend
      .from(VICTORY_DECLARATIONS_TABLE)
      .select("crew_id, user_id, declared_at, dissolves_at");

    if (response.error) throw response.error;

    return {
      declarations: [
        ...((response.data || []) as CrewVictoryDeclarationRecord[]),
        ...localDeclarations,
      ],
      storageAvailable: true,
    };
  } catch (error) {
    console.warn("Could not load crew victory declarations:", error);
    return {
      declarations: localDeclarations,
      storageAvailable: false,
    };
  }
}

function uniqueCompletedIslandProgress(
  progress: IslandProgressRecord[],
  islandIds: Set<string>,
) {
  const byIsland = new Map<string, IslandProgressRecord>();

  progress.forEach((entry) => {
    if (!islandIds.has(entry.island_id)) return;

    const existing = byIsland.get(entry.island_id);
    if (!existing || toTime(entry.completed_at) > toTime(existing.completed_at)) {
      byIsland.set(entry.island_id, entry);
    }
  });

  return Array.from(byIsland.values());
}

function safeScore(value: number | null | undefined) {
  const score = Number(value ?? 0);
  return Number.isFinite(score) ? Math.max(0, Math.trunc(score)) : 0;
}

function normalizeStationKey(value?: string | null) {
  return value?.trim().toLowerCase().replace(/-/g, "_") ?? "";
}

function userIslandKey(userId: string, islandId: string) {
  return `${userId}|${islandId}`;
}

function stationScoresByUserIsland(
  stations: StationRecord[],
  stationProgress: StationProgressRecord[],
) {
  const stationIslandById = new Map(
    stations.map((station) => [station.id, station.island_id]),
  );
  const scores = new Map<string, number>();

  stationProgress.forEach((progress) => {
    const islandId = stationIslandById.get(progress.station_id);
    if (!islandId) return;

    const key = userIslandKey(progress.user_id, islandId);
    scores.set(key, (scores.get(key) ?? 0) + safeScore(progress.station_points));
  });

  return scores;
}

function localStoredStationProgressForUser(
  userId: string | null | undefined,
  islands: IslandRecord[],
  stations: StationRecord[],
) {
  if (!userId || typeof window === "undefined") return [];

  const rows: StationProgressRecord[] = [];

  islands.forEach((island) => {
    const routeStationIds = getQuestionBankStationIdsForIsland(island.slug);
    if (!routeStationIds?.length) return;

    routeStationIds.forEach((routeStationId) => {
      const snapshot = loadStoredPlaytestProgress(
        window.localStorage,
        getPlaytestProgressKey({
          userId,
          islandSlug: island.slug,
          stationId: routeStationId,
        }),
      );
      if (!snapshot) return;

      const stationPoints = safeScore(
        snapshot.totalScore ?? snapshot.session?.stationPoints,
      );
      if (stationPoints <= 0) return;

      const routeKey = normalizeStationKey(routeStationId);
      const remoteStation =
        stations.find(
          (station) =>
            station.island_id === island.id &&
            [station.id, station.slug, station.source_station_id].some(
              (value) => normalizeStationKey(value) === routeKey,
            ),
        ) ?? null;
      const completedStages = new Set(snapshot.completedStages ?? []);
      const isComplete =
        [1, 2, 3].every((stage) => completedStages.has(stage)) ||
        Boolean(snapshot.session?.isComplete);

      rows.push({
        user_id: userId,
        station_id: remoteStation?.id ?? routeStationId,
        station_points: stationPoints,
        completed_at: isComplete ? new Date().toISOString() : null,
      });
    });
  });

  return rows;
}

function mergeStationProgress(
  remoteProgress: StationProgressRecord[],
  localProgress: StationProgressRecord[],
) {
  const byUserStation = new Map<string, StationProgressRecord>();

  [...remoteProgress, ...localProgress].forEach((progress) => {
    const key = `${progress.user_id}|${progress.station_id}`;
    const existing = byUserStation.get(key);
    if (
      !existing ||
      safeScore(progress.station_points) > safeScore(existing.station_points) ||
      (!existing.completed_at && progress.completed_at)
    ) {
      byUserStation.set(key, progress);
    }
  });

  return Array.from(byUserStation.values());
}

function completedIslandProgressForUser(
  userId: string,
  relevantIslands: IslandRecord[],
  stations: StationRecord[],
  islandProgress: IslandProgressRecord[],
  stationProgress: StationProgressRecord[],
) {
  const relevantIslandIds = new Set(relevantIslands.map((island) => island.id));
  const completedProgress = uniqueCompletedIslandProgress(
    islandProgress.filter((entry) => entry.user_id === userId),
    relevantIslandIds,
  );
  const completedByIsland = new Map(
    completedProgress.map((entry) => [entry.island_id, entry]),
  );
  const progressByStationId = new Map(
    stationProgress
      .filter((entry) => entry.user_id === userId)
      .map((entry) => [entry.station_id, entry]),
  );

  relevantIslands.forEach((island) => {
    if (completedByIsland.has(island.id)) return;

    const islandStations = stations.filter(
      (station) =>
        station.island_id === island.id &&
        isQuestionBankStationForIsland(island.slug, station),
    );
    if (islandStations.length === 0) return;

    const stationRows = islandStations.map((station) =>
      progressByStationId.get(station.id),
    );
    if (!stationRows.every((progress) => Boolean(progress?.completed_at))) {
      return;
    }

    completedByIsland.set(island.id, {
      user_id: userId,
      island_id: island.id,
      score: stationRows.reduce(
        (sum, progress) => sum + safeScore(progress?.station_points),
        0,
      ),
      completed_at:
        stationRows
          .map((progress) => progress?.completed_at ?? null)
          .filter(Boolean)
          .sort()
          .at(-1) ?? new Date().toISOString(),
    });
  });

  return Array.from(completedByIsland.values());
}

function totalScoreForUser(options: {
  userId: string;
  relevantIslands: IslandRecord[];
  islandProgress: IslandProgressRecord[];
  stationScoreByUserIsland: Map<string, number>;
  dailyBonusTotals: Map<string, number>;
}) {
  return (
    options.relevantIslands.reduce((sum, island) => {
      const islandScore = Math.max(
        0,
        ...options.islandProgress
          .filter(
            (entry) =>
              entry.user_id === options.userId && entry.island_id === island.id,
          )
          .map((entry) => safeScore(entry.score)),
      );
      const stationScore =
        options.stationScoreByUserIsland.get(
          userIslandKey(options.userId, island.id),
        ) ?? 0;

      return sum + Math.max(islandScore, stationScore);
    }, 0) + (options.dailyBonusTotals.get(options.userId) ?? 0)
  );
}

function assessmentRate(attempt: SelfAssessmentAttemptRecord | null) {
  if (!attempt || attempt.total_questions <= 0) return null;
  return (attempt.score / attempt.total_questions) * 100;
}

function sortByNewestAttempt(
  left: SelfAssessmentAttemptRecord,
  right: SelfAssessmentAttemptRecord,
) {
  return toTime(right.completed_at) - toTime(left.completed_at);
}

function isFinalAssessmentAttempt(
  attempt: SelfAssessmentAttemptRecord,
  finalGateOpenedAt: string | null,
) {
  if (attempt.assessment_type === "final") return true;
  if (attempt.assessment_type === "initial") return false;

  return (
    finalGateOpenedAt !== null &&
    toTime(attempt.completed_at) >= toTime(finalGateOpenedAt)
  );
}

export async function loadCrewVictoryBoard(
  currentUserId?: string | null,
): Promise<CrewVictoryBoard> {
  const [
    crewsResponse,
    membersResponse,
    profilesResponse,
    islandsResponse,
    stationsResponse,
    declarationsResult,
    dailyBonusResult,
  ] = await Promise.all([
    backend.from("crews").select("id, name, owner_id, created_at"),
    backend.from("crew_members").select("crew_id, user_id"),
    backend.from("profiles").select("id, display_name, avatar_url, hub"),
    backend.from("islands").select("id, slug, crew_id, is_active, order_index"),
    backend.from("stations").select("id, island_id, slug, source_station_id"),
    loadVictoryDeclarations(),
    loadDailyStreakBonusTotals(),
  ]);

  assertNoError(crewsResponse, "Could not load crews");
  assertNoError(membersResponse, "Could not load crew members");
  assertNoError(profilesResponse, "Could not load profiles");
  assertNoError(islandsResponse, "Could not load islands");
  assertNoError(stationsResponse, "Could not load stations");

  const crews = (crewsResponse.data || []) as CrewRecord[];
  const members = (membersResponse.data || []) as CrewMemberRecord[];
  const profiles = (profilesResponse.data || []) as ProfileRecord[];
  const islands = ((islandsResponse.data || []) as IslandRecord[])
    .filter((island) => island.is_active !== false)
    .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
  const stations = (stationsResponse.data || []) as StationRecord[];
  const userIds = Array.from(
    new Set(profiles.map((profile) => profile.id).filter(Boolean)),
  );
  const [progressResponse, stationProgressResponse, attemptsResponse] =
    userIds.length > 0
      ? await Promise.all([
          backend
            .from("island_progress")
            .select("user_id, island_id, score, completed_at")
            .in("user_id", userIds),
          backend
            .from("station_progress")
            .select("user_id, station_id, station_points, completed_at")
            .in("user_id", userIds),
          backend
            .from("self_assessment_attempts")
            .select(
              "user_id, score, total_questions, completed_at, assessment_type",
            )
            .in("user_id", userIds),
        ])
      : [
          { data: [], error: null },
          { data: [], error: null },
          { data: [], error: null },
        ];

  assertNoError(progressResponse, "Could not load island progress");
  assertNoError(stationProgressResponse, "Could not load station progress");
  assertNoError(attemptsResponse, "Could not load self assessment attempts");

  const progress = (progressResponse.data || []) as IslandProgressRecord[];
  const remoteStationProgress = (stationProgressResponse.data ||
    []) as StationProgressRecord[];
  const stationProgress = mergeStationProgress(
    remoteStationProgress,
    localStoredStationProgressForUser(currentUserId, islands, stations),
  );
  const attempts = (attemptsResponse.data || []) as SelfAssessmentAttemptRecord[];
  const declarations = declarationMap(declarationsResult.declarations);
  const dailyBonusTotals = dailyBonusResult.totals;
  const stationScoreByUserIsland = stationScoresByUserIsland(
    stations,
    stationProgress,
  );

  const profileById = new Map(profiles.map((profile) => [profile.id, profile]));
  const globalIslands = islands.filter((island) => !island.crew_id);

  const crewStatuses = crews.map((crew): CrewVictoryStatus => {
    const crewMembers = members.filter((member) => member.crew_id === crew.id);
    const crewSpecificIslands = islands.filter(
      (island) => island.crew_id === crew.id,
    );
    const relevantIslands =
      crewSpecificIslands.length > 0 ? crewSpecificIslands : globalIslands;

    const victoryMembers = crewMembers.map((member): CrewVictoryMember => {
      const profile = profileById.get(member.user_id);
      const memberProgress = completedIslandProgressForUser(
        member.user_id,
        relevantIslands,
        stations,
        progress,
        stationProgress,
      );
      const islandsCompleted = memberProgress.length;
      const completedAllIslands =
        relevantIslands.length > 0 && islandsCompleted >= relevantIslands.length;
      const islandCompletionAt = completedAllIslands
        ? latestDate(memberProgress.map((entry) => entry.completed_at))
        : null;
      const totalScore = totalScoreForUser({
        userId: member.user_id,
        relevantIslands,
        islandProgress: progress,
        stationScoreByUserIsland,
        dailyBonusTotals,
      });
      const dailyStreakBonusScore = dailyBonusTotals.get(member.user_id) ?? 0;

      return {
        user_id: member.user_id,
        display_name: profile?.display_name ?? null,
        avatar_url: profile?.avatar_url ?? null,
        hub: profile?.hub ?? null,
        total_score: totalScore,
        daily_streak_bonus_score: dailyStreakBonusScore,
        islands_completed: islandsCompleted,
        total_islands: relevantIslands.length,
        completed_all_islands: completedAllIslands,
        island_completion_at: islandCompletionAt,
        final_assessment_score: null,
        final_assessment_total: null,
        final_assessment_rate: null,
        final_assessment_completed_at: null,
        final_assessment_passed: false,
        victory_declared: false,
        victory_declared_at: null,
      };
    });

    const completedAllIslands =
      crewMembers.length >= 1 &&
      relevantIslands.length > 0 &&
      victoryMembers.every((member) => member.completed_all_islands);
    const crewCompletionAt = completedAllIslands
      ? latestDate(victoryMembers.map((member) => member.island_completion_at))
      : null;

    const membersWithFinalAssessment = victoryMembers.map((member) => {
      const latestFinalAttempt =
        crewCompletionAt === null
          ? null
          : attempts
              .filter(
                (attempt) =>
                  attempt.user_id === member.user_id &&
                  isFinalAssessmentAttempt(attempt, crewCompletionAt),
              )
              .sort(sortByNewestAttempt)[0] ?? null;
      const rate = assessmentRate(latestFinalAttempt);

      return {
        ...member,
        final_assessment_score: latestFinalAttempt?.score ?? null,
        final_assessment_total: latestFinalAttempt?.total_questions ?? null,
        final_assessment_rate: rate,
        final_assessment_completed_at:
          latestFinalAttempt?.completed_at ?? null,
        final_assessment_passed:
          rate !== null && rate >= FINAL_ASSESSMENT_PASS_RATE,
      };
    });

    const finalAssessmentComplete =
      completedAllIslands &&
      membersWithFinalAssessment.every(
        (member) => member.final_assessment_passed,
      );
    const membersWithDeclarations = membersWithFinalAssessment.map((member) => {
      const declaration = validMemberDeclaration(declarations, crew.id, member);
      return {
        ...member,
        victory_declared: Boolean(declaration),
        victory_declared_at: declaration?.declared_at ?? null,
      };
    });
    const allMembersDeclaredVictory =
      finalAssessmentComplete &&
      membersWithDeclarations.every((member) => member.victory_declared);
    const declaredAt = allMembersDeclaredVictory
      ? latestDate(
          membersWithDeclarations.map((member) => member.victory_declared_at),
        )
      : null;
    const victoryDissolvesAt = declaredAt
      ? addDaysIso(declaredAt, VICTORY_DISSOLUTION_DAYS)
      : null;

    return {
      crew_id: crew.id,
      crew_name: crew.name,
      owner_id: crew.owner_id,
      is_solo: false,
      member_count: crewMembers.length,
      total_score: membersWithFinalAssessment.reduce(
        (sum, member) => sum + member.total_score,
        0,
      ),
      total_islands: relevantIslands.length,
      completed_all_islands: completedAllIslands,
      crew_completion_at: crewCompletionAt,
      final_assessment_complete: finalAssessmentComplete,
      victory_declaration_open:
        finalAssessmentComplete && !allMembersDeclaredVictory,
      winner_declared: allMembersDeclaredVictory,
      declared_at: declaredAt,
      victory_dissolves_at: victoryDissolvesAt,
      victory_dissolved:
        victoryDissolvesAt !== null && toTime(victoryDissolvesAt) <= Date.now(),
      members: membersWithDeclarations,
    };
  });

  const crewMemberUserIds = new Set(members.map((member) => member.user_id));
  const soloStatuses = profiles
    .filter((profile) => !crewMemberUserIds.has(profile.id))
    .map((profile): CrewVictoryStatus => {
      const relevantIslands = globalIslands;
      const memberProgress = completedIslandProgressForUser(
        profile.id,
        relevantIslands,
        stations,
        progress,
        stationProgress,
      );
      const islandsCompleted = memberProgress.length;
      const completedAllIslands =
        relevantIslands.length > 0 && islandsCompleted >= relevantIslands.length;
      const islandCompletionAt = completedAllIslands
        ? latestDate(memberProgress.map((entry) => entry.completed_at))
        : null;
      const totalScore = totalScoreForUser({
        userId: profile.id,
        relevantIslands,
        islandProgress: progress,
        stationScoreByUserIsland,
        dailyBonusTotals,
      });
      const dailyStreakBonusScore = dailyBonusTotals.get(profile.id) ?? 0;
      const latestFinalAttempt =
        islandCompletionAt === null
          ? null
          : attempts
              .filter(
                (attempt) =>
                  attempt.user_id === profile.id &&
                  isFinalAssessmentAttempt(attempt, islandCompletionAt),
              )
              .sort(sortByNewestAttempt)[0] ?? null;
      const rate = assessmentRate(latestFinalAttempt);
      const finalAssessmentPassed =
        rate !== null && rate >= FINAL_ASSESSMENT_PASS_RATE;
      const member: CrewVictoryMember = {
        user_id: profile.id,
        display_name: profile.display_name,
        avatar_url: profile.avatar_url,
        hub: profile.hub,
        total_score: totalScore,
        daily_streak_bonus_score: dailyStreakBonusScore,
        islands_completed: islandsCompleted,
        total_islands: relevantIslands.length,
        completed_all_islands: completedAllIslands,
        island_completion_at: islandCompletionAt,
        final_assessment_score: latestFinalAttempt?.score ?? null,
        final_assessment_total: latestFinalAttempt?.total_questions ?? null,
        final_assessment_rate: rate,
        final_assessment_completed_at:
          latestFinalAttempt?.completed_at ?? null,
        final_assessment_passed: finalAssessmentPassed,
        victory_declared: false,
        victory_declared_at: null,
      };
      const finalAssessmentComplete =
        completedAllIslands && finalAssessmentPassed;
      const soloCrewId = `solo:${profile.id}`;
      const declaration = validMemberDeclaration(
        declarations,
        soloCrewId,
        member,
      );
      const memberWithDeclaration = {
        ...member,
        victory_declared: Boolean(declaration),
        victory_declared_at: declaration?.declared_at ?? null,
      };
      const winnerDeclared = finalAssessmentComplete && Boolean(declaration);
      const declaredAt = winnerDeclared
        ? declaration?.declared_at ?? latestFinalAttempt?.completed_at ?? null
        : null;
      const victoryDissolvesAt = declaredAt
        ? addDaysIso(declaredAt, VICTORY_DISSOLUTION_DAYS)
        : null;

      return {
        crew_id: soloCrewId,
        crew_name: profile.display_name || "Explorer",
        owner_id: profile.id,
        is_solo: true,
        member_count: 1,
        total_score: totalScore,
        total_islands: relevantIslands.length,
        completed_all_islands: completedAllIslands,
        crew_completion_at: islandCompletionAt,
        final_assessment_complete: finalAssessmentComplete,
        victory_declaration_open: finalAssessmentComplete && !winnerDeclared,
        winner_declared: winnerDeclared,
        declared_at: declaredAt,
        victory_dissolves_at: victoryDissolvesAt,
        victory_dissolved:
          victoryDissolvesAt !== null && toTime(victoryDissolvesAt) <= Date.now(),
        members: [memberWithDeclaration],
      };
    });

  const sortedCrews = [...crewStatuses, ...soloStatuses].sort((left, right) => {
    if (left.total_score !== right.total_score) {
      return right.total_score - left.total_score;
    }
    const leftCompletion = crewCompletionScore(left);
    const rightCompletion = crewCompletionScore(right);
    if (leftCompletion !== rightCompletion) return rightCompletion - leftCompletion;

    const leftFinal = crewFinalAssessmentScore(left);
    const rightFinal = crewFinalAssessmentScore(right);
    if (leftFinal !== rightFinal) return rightFinal - leftFinal;

    return toTime(left.declared_at) - toTime(right.declared_at);
  });
  const declaredWinners = sortedCrews
    .filter((crew) => crew.winner_declared)
    .sort((left, right) => toTime(left.declared_at) - toTime(right.declared_at));

  return {
    crews: sortedCrews,
    winner: declaredWinners[0] ?? null,
    currentUserCrew:
      sortedCrews.find((crew) =>
        crew.members.some((member) => member.user_id === currentUserId),
      ) ?? null,
    declarationStorageAvailable: declarationsResult.storageAvailable,
  };
}

export async function loadCrewFinalStatusForUser(userId: string) {
  const board = await loadCrewVictoryBoard(userId);
  return board.currentUserCrew;
}

export async function declareCrewVictory(crewId: string, userId: string) {
  const declaredAt = new Date().toISOString();
  const record: CrewVictoryDeclarationRecord = {
    crew_id: crewId,
    user_id: userId,
    declared_at: declaredAt,
    dissolves_at: addDaysIso(declaredAt, VICTORY_DISSOLUTION_DAYS),
  };

  try {
    const { error } = await backend
      .from(VICTORY_DECLARATIONS_TABLE)
      .upsert(record, { onConflict: "crew_id,user_id" });
    if (error) throw error;
    return { storedLocally: false };
  } catch (error) {
    console.warn("Could not persist victory declaration remotely:", error);
    writeLocalDeclaration(record);
    return { storedLocally: true };
  }
}

export function crewCompletionScore(crew: CrewVictoryStatus) {
  const required = Math.max(crew.member_count * crew.total_islands, 1);
  const completed = crew.members.reduce(
    (sum, member) => sum + member.islands_completed,
    0,
  );
  return Math.min(100, Math.round((completed / required) * 100));
}

export function crewFinalAssessmentScore(crew: CrewVictoryStatus) {
  const required = Math.max(crew.member_count, 1);
  const completed = crew.members.filter(
    (member) => member.final_assessment_passed,
  ).length;
  return Math.min(100, Math.round((completed / required) * 100));
}
